import { useEffect, useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Crown, Sparkles, X } from "lucide-react";

export function LoginMembershipPrompt({ workspace, sessionKey }: { workspace: "customer" | "partner" | "owner"; sessionKey: string }) {
  const storageKey = useMemo(() => `shields-membership-prompt:${sessionKey}`, [sessionKey]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (workspace === "owner" || !sessionKey || typeof window === "undefined") return;
    if (window.sessionStorage.getItem(storageKey) !== "dismissed") setOpen(true);
  }, [storageKey, workspace, sessionKey]);

  if (workspace === "owner" || !open) return null;

  const close = () => {
    try { window.sessionStorage.setItem(storageKey, "dismissed"); } catch {}
    setOpen(false);
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-[#07111f]/45 p-3 pb-[calc(1rem+env(safe-area-inset-bottom))] backdrop-blur-[2px] sm:items-center" role="dialog" aria-modal="true" aria-labelledby="membership-prompt-title">
      <div className="relative w-full max-w-md overflow-hidden rounded-[28px] border border-[#e7dcc5] bg-white shadow-[0_30px_100px_-38px_rgba(5,25,55,.55)]">
        <div className="relative bg-[radial-gradient(circle_at_top_right,rgba(230,188,100,.24),transparent_35%),linear-gradient(145deg,#fdfbf6,#f4f9ff)] px-6 pb-6 pt-7">
          <button onClick={close} className="absolute right-4 top-4 z-10 grid h-10 w-10 place-items-center rounded-full border border-[#dbe6ef] bg-white text-[#0a2d55] shadow-sm" aria-label="Close membership offer"><X className="h-5 w-5" /></button>
          <span className="inline-flex items-center gap-2 rounded-full border border-[#d7b66c]/35 bg-[#fff8e8] px-3 py-1 text-[11px] font-black uppercase tracking-[.16em] text-[#a97619]"><Crown className="h-3.5 w-3.5" /> Unlock more Shields</span>
          <h2 id="membership-prompt-title" className="mt-5 pr-10 text-2xl font-black tracking-tight text-[#09284c]">Make Shields work harder for you.</h2>
          <p className="mt-2 text-sm leading-6 text-[#58708a]">Keep the free essentials, or upgrade for richer planning, automation and premium tools. You can close this and carry on anytime.</p>
          <div className="mt-5 grid gap-2 text-sm text-[#183b61]">
            <p className="flex items-center gap-2"><Sparkles className="h-4 w-4 shrink-0 text-[#c89435]" /> More planning and smart tools</p>
            <p className="flex items-center gap-2"><Sparkles className="h-4 w-4 shrink-0 text-[#c89435]" /> Better business or home controls</p>
            <p className="flex items-center gap-2"><Sparkles className="h-4 w-4 shrink-0 text-[#c89435]" /> Upgrade only when it adds value</p>
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <button onClick={close} className="min-h-12 rounded-2xl border border-[#dbe6ef] bg-white text-sm font-extrabold text-[#244a70]">Not now</button>
            <Link to="/plans" onClick={close} className="grid min-h-12 place-items-center rounded-2xl bg-[#0b4da2] px-4 text-center text-sm font-extrabold text-white shadow-[0_12px_30px_-18px_rgba(11,77,162,.85)]">View memberships</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
